import {
  sedrApi,
  type SedrGuestInvite,
  type SedrMember,
  type SedrReportRecipient,
} from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { sedrErrorMessageKey } from './sedrErrors';

export function showSedrReportAddRecipientDialog(options: {
  roomId: string;
  members: SedrMember[];
  guests: SedrGuestInvite[];
  onAdded: (recipient: SedrReportRecipient) => void | Promise<void>;
}): void {
  const shell = createModalShell({
    titleKey: 'sedr.scheduledReport.addRecipientTitle',
    titleId: 'sedr-report-add-title',
    helpTopic: dialogHelpTopic('sedrReportAddRecipient'),
  });

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-report-add-form">
      <p class="muted" data-i18n="sedr.scheduledReport.addRecipientHint"></p>
      <label>
        <span data-i18n="sedr.scheduledReport.addFrom"></span>
        <select id="sedr-report-pick" name="pick"></select>
      </label>
      <label>
        <span data-i18n="sedr.scheduledReport.externalName"></span>
        <input type="text" id="sedr-report-ext-name" name="externalName" autocomplete="name" />
      </label>
      <label>
        <span data-i18n="sedr.scheduledReport.externalEmail"></span>
        <input type="email" id="sedr-report-ext-email" name="externalEmail" autocomplete="email" />
      </label>
      <p class="error-text" id="sedr-report-add-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.setAttribute('data-i18n', 'common.cancel');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'sedr-report-add-form');
  submitButton.setAttribute('data-i18n', 'sedr.scheduledReport.addRecipient');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#sedr-report-add-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#sedr-report-add-error') as HTMLParagraphElement;
  const pickEl = shell.body.querySelector('#sedr-report-pick') as HTMLSelectElement;
  let submitting = false;
  let lastErrorKey = 'status.error';

  pickEl.innerHTML = `<option value="">${t('sedr.scheduledReport.pickPlaceholder')}</option>`;
  for (const member of options.members) {
    if (!member.email) continue;
    const opt = document.createElement('option');
    opt.value = `user:${member.userId}`;
    opt.textContent = `${member.displayName} <${member.email}>`;
    pickEl.append(opt);
  }
  for (const guest of options.guests) {
    if (!guest.email || guest.revokedAt) continue;
    const opt = document.createElement('option');
    opt.value = `guest:${guest.id}`;
    opt.textContent = `${guest.displayName} <${guest.email}>`;
    pickEl.append(opt);
  }

  const refreshLocale = () => {
    if (!errorEl.hidden) {
      errorEl.textContent = t(lastErrorKey);
    }
    applyI18n(shell.dialog);
  };
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    if (submitting) return;
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);
  pickEl.focus();

  const showError = (key: string) => {
    lastErrorKey = key;
    errorEl.hidden = false;
    errorEl.textContent = t(key);
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submitting) return;

    void (async () => {
      errorEl.hidden = true;
      const pick = pickEl.value;
      const extName = (shell.body.querySelector('#sedr-report-ext-name') as HTMLInputElement).value.trim();
      const extEmail = (shell.body.querySelector('#sedr-report-ext-email') as HTMLInputElement).value.trim();

      let payload: {
        email: string;
        displayName: string;
        userId?: string | null;
        guestInviteId?: string | null;
      } | null = null;

      if (pick.startsWith('user:')) {
        const userId = pick.slice(5);
        const member = options.members.find((m) => m.userId === userId);
        if (!member?.email) {
          showError('sedr.scheduledReport.invalidRecipient');
          return;
        }
        payload = {
          email: member.email,
          displayName: member.displayName,
          userId: member.userId,
        };
      } else if (pick.startsWith('guest:')) {
        const guestId = pick.slice(6);
        const guest = options.guests.find((g) => g.id === guestId);
        if (!guest?.email) {
          showError('sedr.scheduledReport.invalidRecipient');
          return;
        }
        payload = {
          email: guest.email,
          displayName: guest.displayName,
          guestInviteId: guest.id,
        };
      } else if (extName && extEmail) {
        payload = { email: extEmail, displayName: extName };
      } else {
        showError('sedr.scheduledReport.invalidRecipient');
        return;
      }

      submitting = true;
      submitButton.disabled = true;
      try {
        const created = await sedrApi.addReportRecipient(options.roomId, payload);
        submitting = false;
        unbindLocale();
        unbindDismiss();
        shell.backdrop.remove();
        await options.onAdded(created);
      } catch (cause) {
        submitting = false;
        submitButton.disabled = false;
        lastErrorKey = sedrErrorMessageKey(cause);
        showError(lastErrorKey);
      }
    })();
  });
}
